import React, { useState } from 'react';
import { Layout, Menu, Dropdown, Avatar, Button, Typography, Badge } from 'antd';
import {
    TeamOutlined,
    FileTextOutlined,
    UserOutlined,
    BarChartOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    BellOutlined,
    SettingOutlined,
    SafetyOutlined,
    AppstoreOutlined,
    FileProtectOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { UserRole } from '../../types/admin.types';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

interface AdminLayoutProps {
    children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Проверяем права доступа
    const isAdmin = user?.role === UserRole.SUPER_ADMIN;
    const isModerator = user?.role === UserRole.MODERATOR;
    const isSignatory = user?.role === UserRole.SIGNATORY;

    const handleLogout = async () => {
        // Очищаем токены
        localStorage.removeItem('accessToken');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        await logout();
        navigate('/admin');
    };

    // Меню элементы в зависимости от роли
    const getMenuItems = () => {
        const items = [];

        // Модерация - доступна модераторам и админам
        if (isModerator || isAdmin) {
            items.push({
                key: '/admin/moderation',
                icon: <TeamOutlined />,
                label: 'Модерация клиентов'
            });
        }

        // Клиенты - доступны модераторам и админам
        if (isModerator || isAdmin) {
            items.push({
                key: '/admin/clients',
                icon: <UserOutlined />,
                label: 'Управление клиентами'
            });
        }

        // Договоры клиентов - доступны модераторам и админам
        if (isModerator || isAdmin) {
            items.push({
                key: '/admin/client-contracts',
                icon: <FileTextOutlined />,
                label: 'Договоры клиентов'
            });
        }

        // Заявки - доступны модераторам и админам
        if (isModerator || isAdmin) {
            items.push({
                key: '/admin/order-signatures',
                icon: <FileProtectOutlined />,
                label: 'Заявки'
            });
        }

        // Справочник номенклатуры - доступен модераторам и админам
        if (isModerator || isAdmin) {
            items.push({
                key: '/admin/products',
                icon: <AppstoreOutlined />,
                label: 'Справочник номенклатуры'
            });
        }

        // Пользователи - только админы
        if (isAdmin) {
            items.push({
                key: '/admin/users',
                icon: <UserOutlined />,
                label: 'Управление пользователями'
            });
        }

        return items;
    };

    const menuItems = getMenuItems();

    const userMenu = {
        items: [
            {
                key: 'profile',
                icon: <UserOutlined />,
                label: 'Профиль'
            },
            {
                key: 'settings',
                icon: <SettingOutlined />,
                label: 'Настройки'
            },
            {
                type: 'divider' as const
            },
            {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Выйти',
                onClick: handleLogout
            }
        ]
    };

    const getRoleBadgeColor = (role: UserRole) => {
        switch (role) {
            case UserRole.SUPER_ADMIN: return 'red';
            case UserRole.MODERATOR: return 'blue';
            case UserRole.SIGNATORY: return 'green';
            default: return 'default';
        }
    };

    const getRoleLabel = (role: UserRole) => {
        switch (role) {
            case UserRole.SUPER_ADMIN: return 'Супер-админ';
            case UserRole.MODERATOR: return 'Модератор';
            case UserRole.SIGNATORY: return 'Подписант';
            default: return role;
        }
    };

    return (
        <Layout className="min-h-screen">
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                width={250}
                className="bg-white shadow-md"
            >
                <div className="flex items-center justify-center h-16 border-b">
                    <div className="flex items-center space-x-2">
                        <SafetyOutlined className="text-2xl text-blue-600" />
                        {!collapsed && (
                            <Title level={4} className="mb-0 text-gray-800">
                                Админ-панель
                            </Title>
                        )}
                    </div>
                </div>

                <Menu
                    mode="inline"
                    selectedKeys={[location.pathname]}
                    items={menuItems}
                    className="border-none"
                    onClick={({ key }) => navigate(key as string)}
                />
            </Sider>

            <Layout>
                <Header className="bg-white px-4 flex justify-between items-center shadow-sm">
                    <div className="flex items-center">
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                            onClick={() => setCollapsed(!collapsed)}
                            className="mr-4"
                        />

                        <div>
                            <Title level={4} className="mb-0">
                                Система "AISER"
                            </Title>
                            <Text type="secondary" className="text-sm">
                                Панель управления заводом
                            </Text>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        {/* Уведомления */}
                        <Badge count={3} size="small">
                            <Button
                                type="text"
                                icon={<BellOutlined />}
                                size="large"
                            />
                        </Badge>

                        {/* Профиль пользователя */}
                        <Dropdown menu={userMenu} trigger={['click']}>
                            <div className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 px-3 py-2 rounded">
                                <Avatar
                                    size="small"
                                    icon={<UserOutlined />}
                                    className="bg-blue-500"
                                />
                                <div className="text-right">
                                    <div className="text-sm font-medium">{user?.fullName || 'Админ'}</div>
                                    <div className="text-xs text-gray-500">
                                        <Badge
                                            color={getRoleBadgeColor(user?.role as UserRole)}
                                            text={getRoleLabel(user?.role as UserRole)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Dropdown>
                    </div>
                </Header>

                <Content className="m-6 p-6 bg-white rounded-lg shadow-sm min-h-0">
                    {children}
                </Content>
            </Layout>
        </Layout>
    );
};
